import { sessionTimeState } from "../components/Clinic/AdminSchedule/calendarUtils";

export function peopleLabel(n) {
  const num = Number(n) || 0;
  const mod10 = num % 10;
  const mod100 = num % 100;
  if (mod10 === 1 && mod100 !== 11) return `${num} человек`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return `${num} человека`;
  }
  return `${num} человек`;
}

/** Прогноз явки на сводке: «буду» + регулярные посетители. */
export function resolveAttendanceForecastDisplay(forecast, rosterFallback = 0) {
  const fc = forecast || {};
  const roster = Number(fc.roster ?? rosterFallback) || 0;
  const confirmed = Number(fc.confirmed) || 0;
  const likely = Number(fc.likely) || 0;
  const notComing = Number(fc.not_coming) || 0;
  const forecastTotal = Number(fc.forecast) || 0;
  const hasSignal = confirmed > 0 || likely > 0 || notComing > 0;

  const value = hasSignal ? forecastTotal : roster;

  const parts = [];
  if (confirmed > 0) parts.push(`${confirmed} сказали «буду»`);
  if (likely > 0) parts.push(`${likely} обычно ходят`);
  if (notComing > 0) parts.push(`${notComing} не придут`);

  let hint;
  if (parts.length) {
    hint = parts.join(" · ");
    if (roster > value) hint += ` · ${peopleLabel(roster)} в группах`;
  } else if (roster > 0) {
    hint = `${peopleLabel(roster)} в группах сегодня`;
  } else {
    hint = "по всем занятиям сегодня";
  }

  const label =
    hasSignal || fc.has_poll ? "Прогноз явки" : "Клиентов ожидается";

  return { value, hint, label };
}

/**
 * Тексты и метрики для блока явки в шторке занятия.
 * sheetOnly — только прогноз по ответам на опрос, без переклички.
 */
export function buildSessionAttendanceInsight({
  session,
  expectationSummary,
  pollStats,
  roll,
  sheetOnly = false,
}) {
  const exp = expectationSummary || pollStats || {};
  const coming = Number(exp.coming ?? pollStats?.forecast_coming ?? 0);
  const notComing = Number(exp.not_coming ?? pollStats?.forecast_not_coming ?? 0);
  const pending = Number(exp.pending ?? 0);
  const total = Number(roll?.total || exp.total || 0);
  const present = Number(roll?.present ?? 0);

  if (sheetOnly) {
    const hasPollData =
      coming > 0 || notComing > 0 || pending > 0 || exp.responded > 0;

    const metrics = [
      coming > 0
        ? { key: "coming", value: coming, label: "придут", tone: "positive" }
        : null,
      notComing > 0
        ? { key: "not", value: notComing, label: "не придут", tone: "negative" }
        : null,
      pending > 0
        ? { key: "pending", value: pending, label: "без ответа", tone: "muted" }
        : null,
    ].filter(Boolean);

    let headline = null;
    let subline = null;

    if (coming > 0) {
      headline = `Придут: ${peopleLabel(coming)}`;
      if (notComing > 0) subline = `Не придут: ${peopleLabel(notComing)}`;
      else if (pending > 0) subline = `Без ответа: ${peopleLabel(pending)}`;
    } else if (pending > 0) {
      headline = `Без ответа: ${peopleLabel(pending)}`;
    } else if (notComing > 0) {
      headline = `Не придут: ${peopleLabel(notComing)}`;
    } else if (total > 0 && !hasPollData) {
      headline = `В группе ${peopleLabel(total)}`;
      subline = "Опрос явки ещё не отправлялся";
    }

    return {
      phase: "forecast",
      tone: coming > 0 ? "positive" : "neutral",
      eyebrow: "Прогноз по ответам",
      headline,
      subline,
      metrics,
      progress: null,
      showIncompleteWarning: false,
      detail: null,
    };
  }

  const marked = Number(roll?.marked ?? 0);
  const unmarked = Math.max(0, total - marked);

  const { isPast, isUpcoming, isLive } = sessionTimeState(session);
  const beforeOrLive = isUpcoming || isLive;

  const metrics = beforeOrLive
    ? [
        coming > 0
          ? { key: "coming", value: coming, label: "придут", tone: "positive" }
          : null,
        notComing > 0
          ? { key: "not", value: notComing, label: "не придут", tone: "negative" }
          : null,
        pending > 0
          ? { key: "pending", value: pending, label: "без ответа", tone: "muted" }
          : null,
      ].filter(Boolean)
    : total > 0
      ? [
          {
            key: "forecast",
            value: coming > 0 ? coming : "—",
            label: "прогноз",
            tone: coming > 0 ? "positive" : "muted",
          },
          {
            key: "actual",
            value: present,
            label: "по факту",
            tone: present > 0 ? "positive" : "muted",
          },
        ]
      : [];

  if (beforeOrLive) {
    let headline = null;
    let subline = null;
    let tone = "neutral";

    if (coming > 0) {
      headline = `Ответили, что придут: ${peopleLabel(coming)}`;
      tone = "positive";
      if (notComing > 0) {
        subline = `Не придут: ${peopleLabel(notComing)}`;
      } else if (pending > 0 && total > coming) {
        subline = `Без ответа: ${peopleLabel(pending)}`;
      }
    } else if (total > 0) {
      headline =
        pending > 0 || exp.responded === 0
          ? `Ждём ответов от ${peopleLabel(total)}`
          : `В группе ${peopleLabel(total)}`;
    }

    return {
      phase: isLive ? "live" : "upcoming",
      tone,
      headline,
      subline,
      metrics,
      progress: null,
      showIncompleteWarning: false,
    };
  }

  // Занятие уже прошло
  const allMarked = total > 0 && marked >= total;
  const expectedComing = coming > 0 ? coming : null;

  if (total > 0 && !allMarked) {
    return {
      phase: "past",
      tone: "warning",
      headline: "Вы не отметили всех",
      subline:
        unmarked > 0
          ? `Осталось отметить: ${peopleLabel(unmarked)}`
          : `Отмечено ${marked} из ${total}`,
      detail:
        expectedComing != null
          ? `Прогноз: ${peopleLabel(expectedComing)}${
              present > 0 ? ` · по факту: ${peopleLabel(present)}` : ""
            }`
          : present > 0
            ? `По факту: ${peopleLabel(present)}`
            : null,
      metrics,
      progress: total ? Math.round((marked / total) * 100) : 0,
      showIncompleteWarning: true,
    };
  }

  if (total > 0 && allMarked) {
    const absent = Math.max(0, marked - present);
    let headline;
    if (expectedComing != null) {
      headline = `Прогноз: ${peopleLabel(expectedComing)} · По факту: ${peopleLabel(present)}`;
    } else {
      headline = `По факту: ${peopleLabel(present)}`;
    }
    let subline = null;
    if (absent > 0) {
      subline = `Не пришли: ${peopleLabel(absent)}`;
    } else if (notComing > 0 && present === 0) {
      subline = `По опросу не придут: ${peopleLabel(notComing)}`;
    }

    return {
      phase: "past",
      tone: present > 0 ? "success" : "neutral",
      headline,
      subline,
      detail: null,
      metrics,
      progress: total ? Math.round((present / total) * 100) : 0,
      showIncompleteWarning: false,
    };
  }

  return {
    phase: "past",
    tone: "neutral",
    headline: null,
    subline: null,
    detail: null,
    metrics,
    progress: null,
    showIncompleteWarning: false,
  };
}
